import React, { useState, useEffect } from 'react';
import { ModalWrapper, ButtonGroup, ProjectDiagnoseCopyModal } from 'components/molecules';
import { PopupProps } from 'react-popup-manager/dist/src';
import { Form, Button, Input, Row, Col, Popconfirm } from 'antd';
import { useProjectDiagnoseDetail } from 'hooks';
import { LectureTarget, RangePicker, Debirefing } from 'components/atoms'
import moment, { Moment } from 'moment';
import { dateFormat } from 'utils/date-utils';
import { PROJECT_WORK_TYPE } from 'commons/constants';
import { RangeValue } from 'rc-picker/lib/interface';
import { getCodeArray } from 'utils/form-utils';
import { fetcher } from 'services';
import CheckDuplicateModal from 'components/molecules/CheckDuplicateModal';
import { usePopupManager } from 'react-popup-manager';

export interface ProjectDiagnoseEditModalProps extends PopupProps {
    group_id: number;
    id: number;
    pType: PROJECT_WORK_TYPE;  
    view_mode?: boolean;     
}

const ProjectDiagnoseEditModal = ({ 
    isOpen, 
    pType,   
    view_mode = false, 
    onClose,         
    id, group_id}: ProjectDiagnoseEditModalProps): React.ReactElement => {
  /* ======================================================
   * states
   * ====================================================== */    
    const [isFormDone, setIsFormDone] = useState<boolean>(false);
    const [diagnoseStartDate, setDiagnoseStartDate] = useState<string>();
    const [diagnoseEndDate, setDiagnoseEndDate] = useState<string>();
    const [studyStartDate, setStudyStartDate] = useState<string>();
    const [studyEndDate, setStudyEndDate] = useState<string>();
    const [debriefingDate, setDebriefingDate] = useState<string>();

  /* ======================================================
  * hooks
  * ====================================================== */
  const { diagnoseInfo, insertSuccess, updateSuccess, deleteSuccess, projectDiagnoseInsert, projectDiagnoseUpdate, projectDiagnoseDelete } = useProjectDiagnoseDetail(id, group_id);
  
   /* ======================================================
   * variables
   * ====================================================== */
   const [isCheckConfirm, setIsCheckConfirm] = useState<boolean>(false);   
   const [checkMessage, setCheckMessage] = useState<string>('');

  const [form] = Form.useForm();
  const { TextArea } = Input;
  const { open } = usePopupManager();   

   /* ======================================================
   * events
   * ====================================================== */
   const onDiagnoseDateChangePeriod = (date: RangeValue<Moment>, dateString: [string, string]) => {
   
    const searchDateS = dateString[0] ? dateFormat(dateString[0], 'YYYY-MM-DD') : undefined;
    const searchDateE = dateString[1] ? dateFormat(dateString[1], 'YYYY-MM-DD') : undefined;

    setDiagnoseStartDate(searchDateS);
    setDiagnoseEndDate(searchDateE);

    if (!searchDateS) {
        form.setFieldValue('diagnose_price', 0)
    }

    onChangeForm();
  };

  const onStudyDateChangePeriod = (date: RangeValue<Moment>, dateString: [string, string]) => {
   
    const searchDateS = dateString[0] ? dateFormat(dateString[0], 'YYYY-MM-DD') : undefined;
    const searchDateE = dateString[1] ? dateFormat(dateString[1], 'YYYY-MM-DD') : undefined;

    setStudyStartDate(searchDateS);
    setStudyEndDate(searchDateE);

    onChangeForm();
  };  

    const onCancel = () => {
        onClose?.(false);
    };

    const onCopy = () => {
        if (!!diagnoseInfo) {
            open(ProjectDiagnoseCopyModal, {
                info: diagnoseInfo, 
                onClose: (isOk)=>{
                    if(isOk) {onClose?.(true)}
                }}); 
        }
    }    

    const onSubmit = (e) => {
        if (!!diagnoseInfo) {
            const debriefing_time = e.debriefing_time;
            let start_time: string | null = null;
            let end_time: string | null = null
            let debriefing_date: string | null = null;

            if (e.debriefing_date) {
                debriefing_date = dateFormat(e.debriefing_date, 'YYYY-MM-DD');
                if (debriefing_time) {
                    start_time = debriefing_date + ' ' +moment(debriefing_time[0]).format('HH:mm'); 
                    end_time = debriefing_date + ' ' +moment(debriefing_time[1]).format('HH:mm');
                }                
            }
            
            const sData = {
                id: id,
                project_group_srl: group_id,
                lecture_target: e.lecture_target.map((code) => code).join(',') || '',
                people_count: e.people_count,
                target_memo: e.target_memo,
                diagnose_start_date: diagnoseStartDate,
                diagnose_end_date: diagnoseEndDate,
                study_start_date: studyStartDate,
                study_end_date: studyEndDate,
                debriefing_date: debriefing_date,
                debriefing_start_time: start_time,
                debriefing_end_time: end_time,
                lecturer_srl: e.lecturer_srl,
                diagnose_price: e.diagnose_price,
                debriefing_price: e.debriefing_price,
                memo: e.memo,
            }            

            if (start_time && end_time && sData.lecturer_srl) {
                // 중복 검사
                fetcher.project.postProjectScheduleCheckD({
                    id: id,
                    lecture_studio: '',
                    lecturer_srl: sData.lecturer_srl,
                    start_time: start_time,
                    end_time: end_time
                })
                .then((res) => {
                    switch (res.code) {
                        case 200:
                            if (id > 0) {
                                projectDiagnoseUpdate(sData)
                            } else {
                                projectDiagnoseInsert(sData)
                            }                                
                        break;
                        default:  
                            setCheckMessage(res.message);
                            setIsCheckConfirm(true);
                        break;
                    };
                });                 
            } else {
                if (id > 0) {
                    projectDiagnoseUpdate(sData)
                } else {
                    projectDiagnoseInsert(sData)
                }  
            }
        }
    };

    const onChangeForm = (changedFields?: any[], allFields?: any[]) => { 
        if (!!changedFields) {
            const debriefing_date = changedFields.filter((field) => field.name[0] === 'debriefing_date');
            if (debriefing_date.length > 0) {
                const searchDate = debriefing_date[0].value ? dateFormat(debriefing_date[0].value, 'YYYY-MM-DD') : undefined;
                setDebriefingDate(searchDate)

                if (!searchDate) {
                    form.setFieldValue('debriefing_price', 0)
                }
            }
        }
        setIsFormDone(true);
    };      

    useEffect(() => {
        if (insertSuccess || updateSuccess || deleteSuccess) {
            onClose?.(true);  
        }
    }, [insertSuccess, updateSuccess, deleteSuccess])

  /* ======================================================
   * effects
   * ====================================================== */
    useEffect(() => {
        if (!!diagnoseInfo) {
            if (!!diagnoseInfo.diagnose_start_date && diagnoseInfo.diagnose_start_date !== '0000-00-00' && !!diagnoseInfo.diagnose_end_date && diagnoseInfo.diagnose_end_date !== '0000-00-00') {
                setDiagnoseStartDate(diagnoseInfo.diagnose_start_date);
                setDiagnoseEndDate(diagnoseInfo.diagnose_end_date);
            }  

            if (!!diagnoseInfo.study_start_date && diagnoseInfo.study_start_date !== '0000-00-00' && !!diagnoseInfo.study_end_date && diagnoseInfo.study_end_date !== '0000-00-00') {
                setStudyStartDate(diagnoseInfo.study_start_date);
                setStudyEndDate(diagnoseInfo.study_end_date);
            }  

            if (!!diagnoseInfo.debriefing_date && diagnoseInfo.debriefing_date !== '0000-00-00') {
                setDebriefingDate(diagnoseInfo.debriefing_date);
            }              

            form.setFieldsValue({
                lecture_target: diagnoseInfo.lecture_target? getCodeArray(diagnoseInfo.lecture_target) : getCodeArray(diagnoseInfo.group_lecture_target),
                people_count: diagnoseInfo.people_count === 0? diagnoseInfo.group_people_count : diagnoseInfo.people_count,
                target_memo: diagnoseInfo.target_memo? diagnoseInfo.target_memo : diagnoseInfo.group_target_memo,
                diagnose_date: (!!diagnoseInfo.diagnose_start_date && diagnoseInfo.diagnose_start_date !== '0000-00-00' && !!diagnoseInfo.diagnose_end_date && diagnoseInfo.diagnose_end_date !== '0000-00-00')?[moment(diagnoseInfo.diagnose_start_date, "YYYY-MM-DD"), moment(diagnoseInfo.diagnose_end_date, "YYYY-MM-DD")]:[], 
                debriefing_date: (!!diagnoseInfo.debriefing_date && diagnoseInfo.debriefing_date !== '0000-00-00') ? moment(diagnoseInfo.debriefing_date, "YYYY-MM-DD") : undefined,
                debriefing_time: diagnoseInfo.debriefing_start_time ? [moment(diagnoseInfo.debriefing_start_time, 'HH:mm'), moment(diagnoseInfo.debriefing_end_time, 'HH:mm')]: [] ,
                study_date: (!!diagnoseInfo.study_start_date && diagnoseInfo.study_start_date !== '0000-00-00' && !!diagnoseInfo.study_end_date && diagnoseInfo.study_end_date !== '0000-00-00')?[moment(diagnoseInfo.study_start_date, "YYYY-MM-DD"), moment(diagnoseInfo.study_end_date, "YYYY-MM-DD")]:[], 
                diagnose_price: diagnoseInfo.diagnose_price,
                debriefing_price: diagnoseInfo.debriefing_price,
                lecturer_srl: diagnoseInfo.lecturer_srl>0?diagnoseInfo.lecturer_srl:null,
                memo: diagnoseInfo.memo,
            });
        }
    }, [diagnoseInfo])

    return (
        <>
        <ModalWrapper open={isOpen} onCancel={onCancel} width={750} title={id===0?'성과백신 진단 일정 등록':'성과백신 진단 일정 수정'} closable>
            <Form
                form={form}
                name="ProjectInfo"
                layout="horizontal"  
                labelCol={{ flex: '120px' }}
                onFieldsChange={onChangeForm}
                onFinish={onSubmit}
                style={{width:'100%'}}
                autoComplete="off"
            >
                <Row>
                    <Col span={9}>
                    <Form.Item label={'고객사(기업명)'}>
                        <label>{diagnoseInfo?.company_name}</label>                  
                    </Form.Item> 
                    </Col> 
                    <Col span={15}> 
                    <Form.Item label={'교육담당자'}>
                        <label>{diagnoseInfo?.crm_name} / {diagnoseInfo?.crm_email} / {diagnoseInfo?.crm_phone}</label>                  
                    </Form.Item>   
                    </Col>            
                </Row>
                <Form.Item  label={'프로젝트명'}>
                    <label>{diagnoseInfo?.group_name}</label>                   
                </Form.Item> 
                <Form.Item name="lecture_target" label={'진단 대상'}>
                    <LectureTarget form={form} />                
                </Form.Item>     
                <Form.Item name="diagnose_date" label={'진단 기간'} rules={[{ type: 'array' as const, required: false, message: '' }]}>
                    <RangePicker format="YYYY-MM-DD" onChange={onDiagnoseDateChangePeriod} style={{width:'250px'}} />                                   
                </Form.Item>  
                {(pType==='confirm') && (
                <>
                    <Form.Item label={'디브리핑'} >
                        <Debirefing form={form} />                                   
                    </Form.Item>                  
                    <Form.Item name="study_date" label={'영상학습 기간'} rules={[{ type: 'array' as const, required: false, message: '' }]}>
                        <RangePicker format="YYYY-MM-DD" onChange={onStudyDateChangePeriod} style={{width:'250px'}} />                                   
                    </Form.Item>                      
                </>
                )}                
                <Form.Item name='diagnose_price' label={'진단 비용'}>
                    <Input type="number" style={{width:'180px'}} disabled={!diagnoseStartDate || !diagnoseEndDate}/>                  
                </Form.Item> 
                <Form.Item name='debriefing_price' label={'디브리핑 비용'}>
                    <Input type="number" style={{width:'180px'}} disabled={!debriefingDate} />                  
                </Form.Item>                 
                <Form.Item name='memo' label={'메모'}>
                    <TextArea rows={3} placeholder='일정 메모를 입력하세요'/>   
                </Form.Item> 
                {(!view_mode) && (
                    <ButtonGroup className={'buttonsWrapper'}>
                        <div>
                            <Button onClick={onCancel} style={{marginRight: "5px"}}>취소</Button>
                        </div>   
                        <div>
                            {(id>0) && (
                                <Popconfirm
                                    title="삭제 하시겠습니까?"
                                    okText="삭제"
                                    cancelText="취소"
                                    onConfirm={() => { projectDiagnoseDelete({id:id})}}
                                >   
                                    <Button danger style={{marginRight: "5px"}}>삭제</Button>        
                                </Popconfirm>
                            )}
                            <Button htmlType="submit"  type="primary" disabled={!isFormDone}>{id===0?'등록':'수정'}</Button>
                        </div>                    
                    </ButtonGroup>    
                )}  
            </Form>
        </ModalWrapper>  
        <CheckDuplicateModal
            title="스케줄 확인"
            message={checkMessage}
            visible={isCheckConfirm}
            onOk={() => setIsCheckConfirm(false)}
        />           
        </>
    )    
};

export default React.memo(ProjectDiagnoseEditModal);    